import { useState } from "react";
import { Check, X, RotateCcw, Trophy, Play } from "lucide-react";
import { useT, QUIZ_Q, quizOpt, chordLabel } from "@/i18n";

const BANK = [
  { q: "What is the relative minor of C Major?", options: ["A minor", "E minor", "D minor", "G minor"], answer: "A minor" },
  { q: "What is the relative minor of G Major?", options: ["E minor", "A minor", "B minor", "D minor"], answer: "E minor" },
  { q: "What is the relative minor of F Major?", options: ["D minor", "A minor", "C minor", "E minor"], answer: "D minor" },
  { q: "Which major key has exactly 1 sharp?", options: ["G Major", "D Major", "F Major", "A Major"], answer: "G Major" },
  { q: "Which major key has exactly 2 sharps?", options: ["D Major", "G Major", "A Major", "Bb Major"], answer: "D Major" },
  { q: "Which major key has exactly 1 flat?", options: ["F Major", "Bb Major", "G Major", "C Major"], answer: "F Major" },
  { q: "Which major key has NO sharps or flats?", options: ["C Major", "G Major", "F Major", "A Major"], answer: "C Major" },
  { q: "The single sharp in G Major is…", options: ["F#", "C#", "G#", "Bb"], answer: "F#" },
  { q: "Playing white keys starting on D gives which mode?", options: ["Dorian", "Ionian", "Phrygian", "Aeolian"], answer: "Dorian" },
  { q: "Playing white keys starting on A gives which mode?", options: ["Aeolian", "Dorian", "Mixolydian", "Lydian"], answer: "Aeolian" },
  { q: "Playing white keys starting on E gives which mode?", options: ["Phrygian", "Lydian", "Ionian", "Dorian"], answer: "Phrygian" },
  { q: "Playing white keys starting on G gives which mode?", options: ["Mixolydian", "Lydian", "Aeolian", "Ionian"], answer: "Mixolydian" },
  { q: "Which mode is simply the major scale?", options: ["Ionian", "Lydian", "Dorian", "Aeolian"], answer: "Ionian" },
  { q: "Which notes make up a C Major triad?", options: ["C – E – G", "C – F – A", "C – E – A", "C – D – G"], answer: "C – E – G" },
  { q: "Which notes make up an A minor triad?", options: ["A – C – E", "A – C# – E", "A – D – F", "A – C – F"], answer: "A – C – E" },
  { q: "Which notes make up a G Major triad?", options: ["G – B – D", "G – Bb – D", "G – C – E", "G – B – E"], answer: "G – B – D" },
  { q: "Moving clockwise on the Circle of Fifths adds a…", options: ["Sharp", "Flat", "Whole step", "Nothing"], answer: "Sharp" },
  { q: "Moving counter-clockwise on the Circle of Fifths adds a…", options: ["Flat", "Sharp", "Half step", "Nothing"], answer: "Flat" },
];

const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);
const ROUND = 6;

export const Quiz = () => {
  const { t, lang } = useT();
  const [phase, setPhase] = useState("intro"); // intro | play | done
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);

  const start = () => {
    setQuestions(
      shuffle(BANK.map((q, i) => ({ ...q, _i: i })))
        .slice(0, ROUND)
        .map((q) => ({ ...q, options: shuffle(q.options) }))
    );
    setIdx(0);
    setPicked(null);
    setScore(0);
    setPhase("play");
  };

  const choose = (opt) => {
    if (picked) return;
    setPicked(opt);
    if (opt === questions[idx].answer) setScore((s) => s + 1);
  };

  const next = () => {
    if (idx + 1 >= questions.length) {
      setPhase("done");
    } else {
      setIdx((i) => i + 1);
      setPicked(null);
    }
  };

  if (phase === "intro") {
    return (
      <div
        className="bg-background text-foreground brutal-border rounded-2xl p-10 max-w-2xl"
        style={{ boxShadow: "6px 6px 0 0 rgba(253,251,247,0.9)" }}
        data-testid="quiz-intro"
      >
        <Trophy className="w-8 h-8 mb-4 text-primary" />
        <h3 className="font-display font-extrabold text-2xl tracking-tight">{t("quiz.ready")}</h3>
        <p className="mt-3 text-muted-foreground leading-relaxed">
          {t("quiz.readyText", { n: ROUND })}
        </p>
        <button
          data-testid="quiz-start-btn"
          onClick={start}
          className="mt-6 font-display font-bold bg-primary text-primary-foreground px-6 py-3.5 rounded-full brutal-border hover:-translate-y-0.5 transition-transform flex items-center gap-2"
        >
          <Play className="w-4 h-4" /> {t("quiz.start")}
        </button>
      </div>
    );
  }

  if (phase === "done") {
    const perfect = score === questions.length;
    const good = score >= Math.ceil(questions.length * 0.6);
    return (
      <div
        className="bg-background text-foreground brutal-border rounded-2xl p-10 max-w-2xl text-left"
        style={{ boxShadow: "6px 6px 0 0 rgba(253,251,247,0.9)" }}
        data-testid="quiz-result"
      >
        <Trophy className="w-9 h-9 mb-4" style={{ color: perfect ? "#66AEFF" : "#FF7A70" }} />
        <h3 className="font-display font-extrabold text-3xl tracking-tight">
          {score} / {questions.length}
        </h3>
        <p className="mt-2 text-lg font-display font-bold">
          {perfect ? t("quiz.perfect") : good ? t("quiz.good") : t("quiz.okay")}
        </p>
        <p className="mt-2 text-muted-foreground">
          {t("quiz.again")}
        </p>
        <button
          data-testid="quiz-restart-btn"
          onClick={start}
          className="mt-6 font-display font-bold bg-primary text-primary-foreground px-6 py-3.5 rounded-full brutal-border hover:-translate-y-0.5 transition-transform flex items-center gap-2"
        >
          <RotateCcw className="w-4 h-4" /> {t("quiz.playAgain")}
        </button>
      </div>
    );
  }

  const cur = questions[idx];
  return (
    <div
      className="bg-background text-foreground brutal-border rounded-2xl p-8 max-w-2xl"
      style={{ boxShadow: "6px 6px 0 0 rgba(253,251,247,0.9)" }}
      data-testid="quiz-card"
    >
      <div className="flex items-center justify-between mb-5">
        <span className="step-tag text-muted-foreground" data-testid="quiz-progress">
          {t("quiz.question")} {idx + 1} / {questions.length}
        </span>
        <span className="step-tag text-primary">{t("quiz.score")} {score}</span>
      </div>

      <div className="w-full h-2 bg-muted rounded-full mb-6 overflow-hidden">
        <div
          className="h-full bg-secondary transition-all duration-300"
          style={{ width: `${((idx + (picked ? 1 : 0)) / questions.length) * 100}%` }}
        />
      </div>

      <h3 className="font-display font-extrabold text-xl sm:text-2xl tracking-tight leading-snug">
        {(QUIZ_Q[lang] && QUIZ_Q[lang][cur._i]) || cur.q}
      </h3>

      <div className="mt-6 grid sm:grid-cols-2 gap-3">
        {cur.options.map((opt) => {
          const isAnswer = opt === cur.answer;
          const isPicked = opt === picked;
          let cls = "bg-card hover:-translate-y-0.5";
          if (picked) {
            if (isAnswer) cls = "bg-[#34C759] text-white";
            else if (isPicked) cls = "bg-primary text-primary-foreground";
            else cls = "bg-card opacity-60";
          }
          return (
            <button
              key={opt}
              data-testid={`quiz-option-${opt}`}
              onClick={() => choose(opt)}
              disabled={!!picked}
              className={`font-display font-bold text-left px-4 py-3.5 rounded-xl brutal-border transition-all flex items-center justify-between gap-2 ${cls}`}
            >
              {chordLabel(quizOpt(opt, lang), lang)}
              {picked && isAnswer && <Check className="w-5 h-5 shrink-0" />}
              {picked && isPicked && !isAnswer && <X className="w-5 h-5 shrink-0" />}
            </button>
          );
        })}
      </div>

      {picked && (
        <button
          data-testid="quiz-next-btn"
          onClick={next}
          className="mt-6 font-display font-bold bg-foreground text-background px-6 py-3 rounded-full hover:-translate-y-0.5 transition-transform"
        >
          {idx + 1 >= questions.length ? t("quiz.results") : t("quiz.next")}
        </button>
      )}
    </div>
  );
};
